import { LeftOutlined, RightOutlined } from "@ant-design/icons"
import { Button } from "antd"

import Flex from "#blocks/Flex"

interface PaginationProps {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
  disabled?: boolean
}

const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
  disabled = false,
}: PaginationProps) => {
  if (totalPages <= 1) return null

  const handlePrevious = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1)
    }
  }

  const handleNext = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1)
    }
  }

  const handlePageClick = (page: number) => {
    onPageChange(page)
  }

  // Generate page numbers to show
  const getPageNumbers = () => {
    const pages: (number | string)[] = []
    const maxVisible = 5

    if (totalPages <= maxVisible) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }
    } else {
      // Show first page
      pages.push(1)

      if (currentPage > 3) {
        pages.push("...")
      }

      // Show pages around current page
      const start = Math.max(2, currentPage - 1)
      const end = Math.min(totalPages - 1, currentPage + 1)

      for (let i = start; i <= end; i++) {
        if (i !== 1 && i !== totalPages) {
          pages.push(i)
        }
      }

      if (currentPage < totalPages - 2) {
        pages.push("...")
      }

      // Show last page
      if (totalPages > 1) {
        pages.push(totalPages)
      }
    }

    return pages
  }

  return (
    <Flex gap={8} style={{ alignItems: "center" }}>
      <Button
        icon={<LeftOutlined />}
        onClick={handlePrevious}
        disabled={disabled || currentPage <= 1}
        size="small"
      >
        Previous
      </Button>

      {getPageNumbers().map((page, index) => (
        <span key={index}>
          {typeof page === "number" ? (
            <Button
              size="small"
              type={page === currentPage ? "primary" : "default"}
              onClick={() => handlePageClick(page)}
              disabled={disabled}
              style={{
                minWidth: 32,
              }}
            >
              {page}
            </Button>
          ) : (
            <span style={{ padding: "0 8px", color: "#999" }}>...</span>
          )}
        </span>
      ))}

      <Button
        icon={<RightOutlined />}
        onClick={handleNext}
        disabled={disabled || currentPage >= totalPages}
        size="small"
        iconPosition="end"
      >
        Next
      </Button>

      <span style={{ marginLeft: 16, color: "#666", fontSize: "14px" }}>
        Page {currentPage} of {totalPages}
      </span>
    </Flex>
  )
}

export default Pagination
